import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share, Plus, MoreVertical, Download } from 'lucide-react';
import { APP_ICON_SRC } from '../utils/appIcons';

/**
 * "Add to Home Screen" invitation.
 *
 * Deliberately withheld on the first visit. Someone who has never used the
 * site has no reason to install it, and asking immediately is the behaviour
 * that trains people to dismiss banners without reading them. It appears from
 * the SECOND visit onward, which is the first moment the request is earned.
 *
 * It also stays out of the way of the two things a first-time visitor is
 * doing: it never renders on desktop (there is no home screen worth the
 * interruption), never once the app is already installed, and waits a beat
 * after load so it isn't competing with the hero.
 *
 * Escalating opt-out: the X dismisses for the session. From the THIRD
 * impression on — i.e. after they have already waved it away twice — a
 * permanent "Don't show again" appears, so a disinterested visitor gets a
 * real exit rather than the same banner every visit forever.
 */

const VISITS_KEY = 'a2hs_visits_v1';
const IMPRESSIONS_KEY = 'a2hs_impressions_v1';
const NEVER_KEY = 'a2hs_never_v1';
const SESSION_SEEN_KEY = 'a2hs_session_v1';

/** Impressions after which the permanent opt-out is offered. */
const OPT_OUT_AFTER = 2;
type Platform = 'ios' | 'android' | null;

/**
 * iOS and Android need completely different instructions, and everything
 * else needs none at all — desktop browsers install from the address bar,
 * where a banner would just be noise.
 */
function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent;
  // iPadOS 13+ reports as Macintosh, so touch support is the tell.
  const isIOS = /iPad|iPhone|iPod/.test(ua) || /Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document;
  if (isIOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return null;
}

/** Already installed — nothing to ask for. */
function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches || (window.navigator as unknown as {
    standalone?: boolean;
  }).standalone === true;
}
type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
  }>;
};
export function AddToHomeScreen() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<Platform>(null);
  const [impressions, setImpressions] = useState(0);
  // Chrome on Android can install natively. When the browser offers us the
  // event we show a real button instead of asking the user to hunt through
  // a menu — instructions are the fallback, not the goal.
  const [installEvent, setInstallEvent] = useState<InstallPromptEvent | null>(null);
  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as InstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    try {
      if (isStandalone()) return;
      if (localStorage.getItem(NEVER_KEY) === '1') return;
      const detected = detectPlatform();
      if (!detected) return;

      // One visit per session, counted on first mount of the session so
      // navigating between pages doesn't inflate the number.
      let visits = parseInt(localStorage.getItem(VISITS_KEY) || '0', 10);
      if (!Number.isFinite(visits) || visits < 0) visits = 0;
      const countedThisSession = sessionStorage.getItem(SESSION_SEEN_KEY) === '1';
      if (!countedThisSession) {
        visits += 1;
        localStorage.setItem(VISITS_KEY, String(visits));
        sessionStorage.setItem(SESSION_SEEN_KEY, '1');
      } else {
        // Already shown (or dismissed) once this session — don't re-open it
        // on every route change.
        return;
      }
      if (visits < 2) return;
      const seen = parseInt(localStorage.getItem(IMPRESSIONS_KEY) || '0', 10);
      const nextImpressions = (Number.isFinite(seen) ? seen : 0) + 1;
      localStorage.setItem(IMPRESSIONS_KEY, String(nextImpressions));
      setImpressions(nextImpressions);
      setPlatform(detected);

      // Let the page settle before interrupting.
      timer = setTimeout(() => setVisible(true), 2500);
    } catch {
      // Private mode / storage disabled — stay silent rather than showing
      // an un-dismissable banner on every page.
    }
    return () => clearTimeout(timer);
  }, []);
  const close = () => setVisible(false);
  const never = () => {
    try {
      localStorage.setItem(NEVER_KEY, '1');
    } catch {}
    setVisible(false);
  };
  const install = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const {
      outcome
    } = await installEvent.userChoice;
    if (outcome === 'accepted') never();else close();
  };
  const showOptOut = impressions > OPT_OUT_AFTER;
  return <AnimatePresence data-id="element-168" data-mp="58iwmyghax7m">
      {visible && platform && <motion.aside
    // Overlays the top of the page rather than sitting in the document
    // flow. The hero pulls itself up 149px so its photo runs behind the
    // navbar, which means ANY block inserted above it in the flow gets
    // covered by that overlap — that is what was clipping this banner in
    // half. Floating it sidesteps the layout entirely: no reflow, no
    // shifted hero, nothing to re-measure. It slides on transform (not
    // height) so a late-loading icon can't strand it at a stale size.
    initial={{
      y: '-100%',
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: '-100%',
      opacity: 0
    }} transition={{
      duration: 0.28,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed inset-x-0 top-0 z-[100] border-b border-stone-200 bg-[#fffcf9] shadow-[0_6px_24px_rgba(0,0,0,0.16)] dark:border-white/10 dark:bg-[#1c1c1c] sm:hidden" aria-label="Add 206Gravel to your home screen" data-id="element-169" data-mp="b0lr2iwi7wz">
          <div className="px-4 py-3.5" data-id="element-170" data-mp="3169rtw2o0r1k">
            <div className="flex items-start gap-3" data-id="element-171" data-mp="317b4g4zbxlle">
              <img src={APP_ICON_SRC} alt="" width={40} height={40} className="mt-0.5 h-10 w-10 shrink-0 rounded-[9px]" data-id="element-172" data-mp="3ow468i2munnx" />
              <div className="min-w-0 flex-1" data-id="element-173" data-mp="3fuw75xsaliss">
                <p className="font-heading text-[15px] font-bold leading-tight text-stone-900 dark:text-white" data-id="element-174" data-mp="3fya7z1lb5jy4">
                  Add 206Gravel to your home screen
                </p>
                <p className="mt-0.5 font-sub text-xs leading-snug text-stone-600 dark:text-white/70" data-id="element-175" data-mp="24fhcx1rgx51d">
                  Reorder a load in two taps — no searching, no typing the
                  address again.
                </p>

                {/* Android with a real install event gets a button; everyone
                    else gets the two taps their browser actually requires,
                    with the menu icons drawn so they can be matched by sight
                    rather than by name. */}
                {installEvent ? <button type="button" onClick={install} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#059F40] px-4 py-2.5 font-sub text-sm font-bold text-white transition-colors duration-150 hover:bg-[#04A05C]" data-id="element-176" data-mp="1i7u1gztddlwt">
                    <Download className="h-4 w-4" aria-hidden="true" data-id="element-177" data-mp="1n76j965dw2sj" />
                    Install app
                  </button> : <ol className="mt-2.5 space-y-1.5 font-sub text-xs text-stone-700 dark:text-white/80" data-id="element-178" data-mp="zfri3gpcfriv">
                    <li className="flex items-center gap-2" data-id="element-179" data-mp="ugyag4wqmuqm">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-stone-200 text-[10px] font-bold text-stone-700 dark:bg-white/15 dark:text-white" data-id="element-180" data-mp="90rc48cgv79x">
                        1
                      </span>
                      {platform === 'ios' ? <span className="flex items-center gap-1.5" data-id="element-181" data-mp="n0ad8h6v1icq">
                          Tap
                          <Share className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-182" data-mp="rxiumfk6picx" />
                          <span className="font-semibold" data-id="element-183" data-mp="wwn5rbqo5q5w">Share</span>
                          <span className="text-stone-500 dark:text-white/55" data-id="element-184" data-mp="11o57i20inurx">
                            in the toolbar
                          </span>
                        </span> : <span className="flex items-center gap-1.5" data-id="element-185" data-mp="1apbnm9z973kn">
                          Tap
                          <MoreVertical className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-186" data-mp="1fnd7uy8f18xp" />
                          <span className="font-semibold" data-id="element-187" data-mp="305ozuabsveo3">Menu</span>
                          <span className="text-stone-500 dark:text-white/55" data-id="element-188" data-mp="303uit73ng6pa">
                            top right
                          </span>
                        </span>}
                    </li>
                    <li className="flex items-center gap-2" data-id="element-189" data-mp="2m6fmrjiw50oc">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-stone-200 text-[10px] font-bold text-stone-700 dark:bg-white/15 dark:text-white" data-id="element-190" data-mp="2m88uz395jsyg">
                        2
                      </span>
                      <span className="flex items-center gap-1.5" data-id="element-191" data-mp="3a3lal10ts7lo">
                        Choose
                        <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-192" data-mp="355jt2kj1sgcb" />
                        <span className="font-semibold" data-id="element-193" data-mp="6f1lnsbls9j5">
                          {platform === 'ios' ? 'Add to Home Screen' : 'Add to Home screen'}
                        </span>
                      </span>
                    </li>
                  </ol>}

                {showOptOut && <button type="button" onClick={never} className="mt-3 font-sub text-xs font-semibold text-stone-500 underline underline-offset-2 transition-colors hover:text-stone-800 dark:text-white/50 dark:hover:text-white" data-id="element-194" data-mp="346jsts69wzjw">
                    Don't show this again
                  </button>}
              </div>

              <button type="button" onClick={close} aria-label="Dismiss" className="-mr-1 -mt-1 shrink-0 rounded-full p-2 text-stone-400 transition-colors duration-150 hover:bg-stone-100 hover:text-stone-700 dark:hover:bg-white/10 dark:hover:text-white" data-id="element-195" data-mp="3n5xruoiv10h9">
                <X className="h-4 w-4" aria-hidden="true" data-id="element-196" data-mp="epvzml456pql" />
              </button>
            </div>
          </div>
        </motion.aside>}
    </AnimatePresence>;
}